import type { ReactNode } from 'react'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/16/solid'
import type { WizardStep } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IssueList, WizardIssue } from '../../../Services/OrderWizard/issues.ts'
import { listedIssues } from '../../../Services/OrderWizard/issues.ts'
import { Callout } from './Primitives.tsx'

const HIGHLIGHT = ['ring-2', 'ring-amber-400', 'ring-offset-2']

/** Scrolls to an issue's card or row and highlights it for a moment. */
export const showTarget = (target: string | undefined) => {
  if (!target) {
    return
  }
  requestAnimationFrame(() => {
    const element = document.getElementById(target)
    if (!element) {
      return
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    element.classList.add(...HIGHLIGHT)
    window.setTimeout(() => element.classList.remove(...HIGHLIGHT), 2000)
  })
}

const IssueRow = ({ issue, action }: { issue: WizardIssue; action?: ReactNode }) => {
  const blocker = issue.severity === 'blocker'
  const Icon = blocker ? XMarkIcon : ExclamationTriangleIcon
  return (
    <Callout as="li" tone={issue.severity} className="rounded-md bg-white px-2 py-1.5 text-sm" data-severity={issue.severity}>
      <button
        type="button"
        className="flex items-start gap-1.5 text-left text-slate-800 hover:underline"
        onClick={() => showTarget(issue.target)}
      >
        <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${blocker ? 'text-red-500' : 'text-amber-500'}`} /> {issue.title}
      </button>
      {action}
      {issue.details && issue.details.length > 0 && (
        <details className="ml-5 text-xs text-slate-600" data-testid="wizard-issue-details">
          <summary className="cursor-pointer">show {issue.details.length === 1 ? 'it' : `all ${issue.details.length}`}</summary>
          {issue.details.join(', ')}
        </details>
      )}
    </Callout>
  )
}

/** `actions`: a fix offered on an issue's row, by issue id. */
export const StepIssues = ({
  issues,
  step,
  actions = {}
}: {
  issues: IssueList
  step: WizardStep
  actions?: Partial<Record<string, ReactNode>>
}) => {
  const listed = listedIssues(issues, step)
  if (listed.length === 0) {
    return null
  }
  return (
    <ul className="space-y-1" data-testid="wizard-step-issues">
      {listed.map((issue) => (
        <IssueRow key={issue.id} issue={issue} action={actions[issue.id]} />
      ))}
    </ul>
  )
}
